import type { ExtraOption, GuidedStep } from './guidedSteps'
import type { GuidedState } from './guidedFlowReducer'

export type SelectedOption = {
  option: ExtraOption
  quantity: number
}

/**
 * Aplana TODAS las opciones seleccionadas (>0) de todos los pasos, en un
 * único array — la lista base que necesitan tanto el subtotal
 * (`guidedSubtotal.ts`) como los dos armadores de línea de carrito
 * (`buildKioskCartLine.ts` / `buildMicrositeCartLine.ts`).
 *
 * Deduplica por `(groupId, optionId)`: hoy `buildGuidedSteps` reparte cada
 * grupo a EXACTAMENTE un balde (una pregunta obligatoria, o uno de
 * add/remove/choiceGroups dentro de `extras`), así que una opción nunca
 * aparece en dos pools a la vez. Pero eso es un invariante de
 * `guidedSteps.ts`, no de esta función — si algún día la clasificación de un
 * grupo dejara de ser todo-o-nada, este guard es lo único que evita que el
 * subtotal pintado quede bien (él ya dedupicaba) mientras la línea de carrito
 * que se cobra duplica el modificador — exactamente la divergencia pintado-
 * vs-cobrado que la suite de paridad existe para atrapar, en la única forma
 * en que no podía verla porque los tres puntos no compartían este cálculo.
 */
export function flattenSelectedOptions(
  steps: GuidedStep[],
  selections: GuidedState['selections']
): SelectedOption[] {
  const seen = new Set<string>()
  const flat: SelectedOption[] = []
  for (const step of steps) {
    const pool =
      step.kind === 'question'
        ? step.options
        : [...step.add, ...step.remove, ...step.choiceGroups.flatMap((c) => c.options)]
    for (const option of pool) {
      const key = `${option.groupId}:${option.optionId}`
      if (seen.has(key)) continue
      seen.add(key)
      const quantity = selections[option.groupId]?.[option.optionId] ?? 0
      if (quantity > 0) flat.push({ option, quantity })
    }
  }
  return flat
}
